import Phaser from 'phaser';
import {
  EventBus, useGameStore, findFurniture, canUseDeskItem, deskSizeOf, deskFitsArea, desksOverlap, furnitureFootprint, itemFitsDesk,
  MAX_DESK_ITEMS, isDeskRug, deskItemCollisions, deskItemDepthOffsets, deskItemLayer,
} from '@campus/core';
import { ensureFurnitureTextures, spawnFurnitureImage } from '../loaders/FurnitureTextures.js';
import { FLOOR_DEPTHS } from '../loaders/FloorRenderer.js';
import { getTenantSlug } from '../../lib/tenant.js';
import { jwtRole, FREE_DESK_PLACEMENT_ROLES } from '../../lib/jwtRole.js';
import { API_BASE } from '../../lib/apiBase.js';

/**
 * PersonalDesks — bureaux personnels en jeu (voir tasks/bureaux-personnels-plan.md).
 *
 * - Rendu des bureaux de la carte (étiquette, léger contour) et de leurs objets
 *   (mêmes sprites et corps de collision que le mobilier : `spawnItem`, fourni
 *   par ZoneScene) — rechargé sur `colyseus:desks-changed`.
 * - Détection de l'espace bureaux / du bureau où se tient l'avatar → store
 *   `personalDeskUi` (affiché par PersonalDeskPrompt).
 * - Emplacements (tasks/emplacements-bureaux-plan.md) : les emplacements libres
 *   sont tracés au sol (« Libre ») ; debout sur l'un d'eux, un membre sans
 *   bureau le prend (« Prendre ce bureau »).
 * - Mode « poser son bureau » (pose libre, OWNER/ADMIN seulement) : rectangle
 *   fantôme vert/rouge sous la souris (ou autour de l'avatar sur écran
 *   tactile), clic ou « Poser ici » pour valider, Échap pour annuler.
 * - Mode « Personnaliser » (son bureau) : brouillon local des objets, posés
 *   d'un clic depuis DeskCustomizePanel, déplacés en cliquant dessus, retirés
 *   avec Suppr ; « Terminer » enregistre tout d'un coup (PUT), « Annuler »
 *   abandonne. Pendant l'édition, les objets du bureau n'ont pas de collision.
 * - Objets : tout bloque sauf sièges et tapis (base seulement pour les objets
 *   hauts) ; un siège passe derrière un bureau sur la même rangée, un objet
 *   posé dessus (écran, lampe) passe devant et ne bloque rien (deskItemCollisions /
 *   deskItemDepthOffsets, @campus/core).
 * - Alerte du propriétaire quand quelqu'un arrive à son bureau.
 *
 * Validation finale côté API (espace, chevauchement entre bureaux) ; les cases
 * bloquantes (murs, eau, meubles, portes) ne sont vérifiées qu'ici.
 */

export interface DeskArea {
  id: string;
  name: string;
  /** En cases. */
  x: number; y: number; width: number; height: number;
  properties: Record<string, unknown>;
}

export interface PersonalDeskDTO {
  id: string;
  userId: string;
  displayName: string;
  areaZoneId: string;
  x: number; y: number;
  tileX: number; tileY: number;
  width: number; height: number;
  outOfArea: boolean;
  items: Array<{ id: string; furnitureId: string; dx: number; dy: number; layer: string; isSolid: boolean; collisionGrid: unknown }>;
}

export interface DeskSlotDTO {
  id: string;
  areaZoneId: string;
  x: number; y: number;
  tileX: number; tileY: number;
  width: number; height: number;
  outOfArea: boolean;
  deskId: string | null;
  ownerName: string | null;
}

export interface SpawnedItem { destroy(): void }

interface Deps {
  tile: number;
  mapId: string;
  localUserId: string | null;
  /** Affiche un objet comme le mobilier de la carte (sprite + corps solides). */
  /**
   * `usable` : le joueur local peut utiliser l'objet (`canUseDeskItem`). Faux → aucune
   * interaction proposée (s'asseoir, tableau blanc, et toute interaction à venir).
   */
  spawnItem: (item: { id: string; furnitureId: string; tileX: number; tileY: number; layer: string; isSolid: boolean; collisionGrid: unknown; depthOffset: number; usable: boolean }) => SpawnedItem | null;
  /** Instantané des cases bloquantes (pris au début du mode « poser »). */
  blockedCells: () => (cx: number, cy: number) => boolean;
  /** Corps de collision changés : replacer l'avatar s'il est coincé, mettre à jour le voile. */
  onLayoutChanged: () => void;
  /** L'avatar entre sur un bureau personnel ou en sort (voile). */
  onCurrentDeskChanged: () => void;
  playerPosition: () => { x: number; y: number } | null;
  remotePlayers: () => Array<{ userId: string; name: string; x: number; y: number }>;
  inputBlocked: () => boolean;
  /** Cadre la caméra sur un point (détache le suivi de l'avatar) / reprend le suivi. */
  focusCamera: (x: number, y: number) => void;
  releaseCamera: () => void;
}

interface DraftItem { furnitureId: string; dx: number; dy: number }

interface Customizing {
  deskId: string;
  draft: DraftItem[];
  /** Objet choisi dans le panneau : chaque clic en pose un exemplaire. */
  selectedId: string | null;
  /** Objet posé ramassé pour être déplacé (`from` = sa place d'origine). */
  carried: (DraftItem & { from: DraftItem }) | null;
  allowed: Set<string>;
  objects: Array<{ destroy(): void }>;
  ghost: Phaser.GameObjects.Image | null;
  ghostKey: string | null;
  grid: Phaser.GameObjects.Graphics;
  saving: boolean;
}

/**
 * Marques au sol (emplacements, cadre des bureaux) : au-dessus de tous les
 * calques de sol, y compris les calques « normaux » d'un fichier Tiled
 * (tmjOther, souvent le sol lui-même), sous le mobilier et les avatars (100+).
 */
const DEPTH_FLOOR_MARKS = FLOOR_DEPTHS.tmjOther + 0.5;
const DEPTH_GHOST = 860;       // au-dessus des avatars et du voile, sous les labels de bureau
const DEPTH_DESK_LABEL = 900;
const COLOR_OK = 0x4ade80;
const COLOR_KO = 0xf07070;
const CLICK_MAX_DRAG_PX = 6;
const VISITOR_CHECK_MS = 500;

export class PersonalDesks {
  private areas: DeskArea[] = [];
  private desks: PersonalDeskDTO[] = [];
  private slots: DeskSlotDTO[] = [];
  private rendered: Array<{ destroy(): void }> = [];
  private generation = 0;
  private currentAreaId: string | null = null;
  private currentDeskId: string | null = null;
  private currentSlotId: string | null = null;
  private currentAreaHasFreeSlot = false;
  private uiStale = false;

  private placing: { areaId: string; blocked: (cx: number, cy: number) => boolean; rect: { x: number; y: number } | null } | null = null;
  private ghost: Phaser.GameObjects.Graphics | null = null;
  private pointerDown: { x: number; y: number } | null = null;
  private readonly touch = typeof window !== 'undefined' && window.matchMedia?.('(pointer: coarse)').matches;

  private customizing: Customizing | null = null;
  private visitorsAtMyDesk = new Set<string>();
  private lastVisitorCheck = 0;
  private cleanups: Array<() => void> = [];

  constructor(private readonly scene: Phaser.Scene, private readonly deps: Deps) {
    const on = <K extends 'ui:personal-desk-place' | 'ui:personal-desk-claim' | 'ui:personal-desk-confirm' | 'ui:personal-desk-cancel' | 'ui:personal-desk-release'
      | 'ui:personal-desk-customize' | 'ui:personal-desk-customize-save' | 'ui:personal-desk-customize-cancel' | 'ui:personal-desk-remove-carried'>(
      event: K, fn: () => void,
    ) => {
      EventBus.on(event, fn);
      this.cleanups.push(() => EventBus.off(event, fn));
    };
    on('ui:personal-desk-customize', () => { void this.startCustomizing(); });
    on('ui:personal-desk-customize-save', () => { void this.saveCustomizing(); });
    on('ui:personal-desk-customize-cancel', () => this.stopCustomizing(true));
    on('ui:personal-desk-remove-carried', () => this.removeCarried());
    const onSelect = ({ furnitureId }: { furnitureId: string | null }) => this.selectFurniture(furnitureId);
    EventBus.on('ui:personal-desk-select', onSelect);
    this.cleanups.push(() => EventBus.off('ui:personal-desk-select', onSelect));
    on('ui:personal-desk-place', () => this.startPlacing());
    on('ui:personal-desk-claim', () => { void this.claim(); });
    on('ui:personal-desk-confirm', () => { void this.confirmPlacing(); });
    on('ui:personal-desk-cancel', () => this.stopPlacing());
    on('ui:personal-desk-release', () => { void this.release(); });

    const onDown = (p: Phaser.Input.Pointer) => { this.pointerDown = { x: p.x, y: p.y }; };
    const onUp = (p: Phaser.Input.Pointer) => {
      const down = this.pointerDown;
      this.pointerDown = null;
      if (!down || this.deps.inputBlocked()) return;
      if (Phaser.Math.Distance.Between(down.x, down.y, p.x, p.y) > CLICK_MAX_DRAG_PX) return; // glisser = déplacer la vue
      if (this.customizing) this.clickCustomizing();
      else if (this.placing && !this.touch) void this.confirmPlacing();
    };
    scene.input.on('pointerdown', onDown);
    scene.input.on('pointerup', onUp);
    this.cleanups.push(() => { scene.input.off('pointerdown', onDown); scene.input.off('pointerup', onUp); });

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
      if (e.key === 'Escape' && this.placing) this.stopPlacing();
      if (this.customizing) this.keyCustomizing(e);
    };
    window.addEventListener('keydown', onKey);
    this.cleanups.push(() => window.removeEventListener('keydown', onKey));

    useGameStore.getState().setPersonalDeskUi({ area: null, desk: null, slot: null, areaHasFreeSlot: false, placing: null });
    void this.loadMine();
  }

  // ── Données ────────────────────────────────────────────────

  /** Espaces bureaux de la carte (map_zones type desk-area), rechargés avec les zones. */
  setAreas(areas: DeskArea[]): void {
    this.areas = areas;
    if (this.placing && !areas.some((a) => a.id === this.placing!.areaId)) this.stopPlacing();
  }

  /** Bureau du joueur sur tout le campus (peut être sur une autre carte). */
  private async loadMine(): Promise<void> {
    const res = await this.api('GET', '/world/desks/personal/mine').catch(() => null);
    if (!res?.ok) return;
    const mine = await res.json() as { id: string; mapId: string } | null;
    useGameStore.getState().setPersonalDeskUi({ myDesk: mine });
  }

  async load(): Promise<void> {
    const mapId = encodeURIComponent(this.deps.mapId);
    const [res, slotsRes] = await Promise.all([
      this.api('GET', `/world/desks/personal?mapId=${mapId}`).catch(() => null),
      this.api('GET', `/world/desks/slots?mapId=${mapId}`).catch(() => null),
    ]);
    if (!res?.ok) return;
    this.desks = await res.json() as PersonalDeskDTO[];
    if (slotsRes?.ok) this.slots = await slotsRes.json() as DeskSlotDTO[];
    if (this.customizing && !this.myDesk()) {
      this.stopCustomizing(false);
      toast('Ton bureau a été retiré par un administrateur.', 'warning');
    }
    await this.render();
    void this.loadMine();
  }

  private async render(): Promise<void> {
    const generation = ++this.generation;
    const visible = this.desks.filter((d) => !d.outOfArea);
    const entries = visible.flatMap((d) => d.items.map((i) => findFurniture(i.furnitureId))).filter((e) => e !== undefined);
    await ensureFurnitureTextures(this.scene, entries);
    if (generation !== this.generation || !this.scene.sys?.displayList) return; // rendu plus récent, ou scène fermée

    for (const obj of this.rendered) obj.destroy();
    this.rendered = [];
    const t = this.deps.tile;
    // Emplacements libres : contour pointillé discret au sol + « Libre ».
    for (const slot of this.slots) {
      if (slot.outOfArea || slot.deskId) continue;
      const g = this.scene.add.graphics().setDepth(DEPTH_FLOOR_MARKS);
      g.fillStyle(0x3a3a50, 0.06);
      g.fillRect(slot.tileX * t, slot.tileY * t, slot.width * t, slot.height * t);
      // Trait sombre doublé d'un trait clair : lisible sur sol clair comme foncé.
      dashedRect(g, slot.tileX * t + 3, slot.tileY * t + 3, slot.width * t - 6, slot.height * t - 6, 0xffffff, 0.5);
      dashedRect(g, slot.tileX * t + 2, slot.tileY * t + 2, slot.width * t - 4, slot.height * t - 4, 0x3a3a50, 0.55);
      const label = this.scene.add.text((slot.tileX + slot.width / 2) * t, (slot.tileY + slot.height / 2) * t, 'Libre', {
        fontSize: '11px', fontStyle: '600', color: '#3a3a50', stroke: '#ffffff', strokeThickness: 3,
        fontFamily: 'Inter, system-ui, sans-serif',
      }).setOrigin(0.5).setAlpha(0.8).setDepth(DEPTH_FLOOR_MARKS).setResolution(2);
      this.rendered.push(g, label);
    }
    for (const desk of visible) {
      const mine = desk.userId === this.deps.localUserId;
      const g = this.scene.add.graphics().setDepth(DEPTH_FLOOR_MARKS);
      g.fillStyle(mine ? COLOR_OK : 0xffffff, mine ? 0.08 : 0.04);
      g.fillRect(desk.tileX * t, desk.tileY * t, desk.width * t, desk.height * t);
      g.lineStyle(1, mine ? COLOR_OK : 0xffffff, mine ? 0.35 : 0.18);
      g.strokeRect(desk.tileX * t + 0.5, desk.tileY * t + 0.5, desk.width * t - 1, desk.height * t - 1);
      // Pastille sombre au texte clair, prénom seul, jamais plus large que le
      // bureau : avant, « Bureau de Prénom Nom » en gris clair sans fond était
      // illisible sur le gazon et chevauchait les bureaux voisins.
      const label = this.scene.add.text((desk.tileX + desk.width / 2) * t, desk.tileY * t - 3, mine ? 'Ton bureau' : firstName(desk.displayName), {
        fontSize: '11px', fontStyle: '600', color: mine ? '#4ade80' : '#ffffff',
        fontFamily: 'Inter, system-ui, sans-serif', backgroundColor: 'rgba(20, 22, 34, 0.82)', padding: { x: 5, y: 1 },
      }).setOrigin(0.5, 1).setDepth(DEPTH_DESK_LABEL).setResolution(2);
      fitLabel(label, desk.width * t - 4);
      this.rendered.push(g, label);

      if (this.customizing?.deskId === desk.id) continue; // brouillon affiché à part, sans collision
      const offsets = deskItemDepthOffsets(desk.items);
      const grids = deskItemCollisions(desk.items);
      desk.items.forEach((item, index) => {
        // Calque, collision et profondeur toujours recalculés depuis l'objet :
        // les règles valent aussi pour les objets posés avant leur ajout.
        const entry = findFurniture(item.furnitureId);
        if (!entry) return;
        const grid = grids[index] ?? null;
        const spawned = this.deps.spawnItem({
          id: `pd-item-${item.id}`,
          furnitureId: item.furnitureId,
          tileX: desk.tileX + item.dx,
          tileY: desk.tileY + item.dy,
          layer: deskItemLayer(entry),
          isSolid: grid !== null,
          collisionGrid: grid,
          depthOffset: offsets[index] ?? 0,
          usable: !!this.deps.localUserId && canUseDeskItem({ ownerUserId: desk.userId }, this.deps.localUserId),
        });
        if (spawned) this.rendered.push(spawned);
      });
    }
    // Bureaux rechargés (posé, libéré, aménagé, supprimé par un admin) : l'invite
    // doit être republiée même si l'avatar n'a pas bougé (ex. son bureau vient
    // d'être libéré sous ses pieds — « aucun bureau » avant comme après).
    this.uiStale = true;
    this.deps.onLayoutChanged();
  }

  // ── Boucle ─────────────────────────────────────────────────

  update(time: number): void {
    const pos = this.deps.playerPosition();
    if (!pos) return;
    const t = this.deps.tile;
    const cx = Math.floor(pos.x / t);
    const cy = Math.floor(pos.y / t);

    const area = this.areas.find((a) => cx >= a.x && cx < a.x + a.width && cy >= a.y && cy < a.y + a.height) ?? null;
    const desk = this.desks.find((d) => !d.outOfArea && cx >= d.tileX && cx < d.tileX + d.width && cy >= d.tileY && cy < d.tileY + d.height) ?? null;
    const slot = desk ? null : this.slots.find((s) => !s.outOfArea && !s.deskId && cx >= s.tileX && cx < s.tileX + s.width && cy >= s.tileY && cy < s.tileY + s.height) ?? null;
    const areaHasFreeSlot = !!area && this.slots.some((s) => s.areaZoneId === area.id && !s.outOfArea && !s.deskId);

    if (this.uiStale || (area?.id ?? null) !== this.currentAreaId || (desk?.id ?? null) !== this.currentDeskId
      || (slot?.id ?? null) !== this.currentSlotId || areaHasFreeSlot !== this.currentAreaHasFreeSlot) {
      this.uiStale = false;
      const deskChanged = (desk?.id ?? null) !== this.currentDeskId;
      this.currentAreaId = area?.id ?? null;
      this.currentDeskId = desk?.id ?? null;
      this.currentSlotId = slot?.id ?? null;
      this.currentAreaHasFreeSlot = areaHasFreeSlot;
      if (deskChanged) this.deps.onCurrentDeskChanged();
      useGameStore.getState().setPersonalDeskUi({
        area: area ? { id: area.id, name: area.name } : null,
        desk: desk ? { id: desk.id, ownerName: desk.displayName, isMine: desk.userId === this.deps.localUserId } : null,
        slot: slot ? { id: slot.id } : null,
        areaHasFreeSlot,
      });
    }

    if (this.placing) this.updateGhost(pos);
    if (this.customizing) this.updateCustomizeGhost();
    if (time - this.lastVisitorCheck > VISITOR_CHECK_MS) {
      this.lastVisitorCheck = time;
      this.checkVisitors();
    }
  }

  /** Rectangle à éclairer (voile) quand l'avatar est sur un bureau personnel, en pixels. */
  litArea(): { x: number; y: number; width: number; height: number } | null {
    const desk = this.desks.find((d) => d.id === this.currentDeskId);
    if (!desk) return null;
    const t = this.deps.tile;
    return { x: desk.tileX * t, y: desk.tileY * t, width: desk.width * t, height: desk.height * t };
  }

  // ── Poser son bureau ───────────────────────────────────────

  /** Pose libre : OWNER/ADMIN seulement (l'API refuse aussi un membre). */
  private canPlaceFreely(): boolean {
    return FREE_DESK_PLACEMENT_ROLES.includes(jwtRole(useGameStore.getState().jwt) ?? '');
  }

  private startPlacing(): void {
    if (!this.currentAreaId || this.placing || !this.canPlaceFreely()) return;
    this.placing = { areaId: this.currentAreaId, blocked: this.deps.blockedCells(), rect: null };
    this.ghost = this.scene.add.graphics().setDepth(DEPTH_GHOST);
    useGameStore.getState().setPersonalDeskUi({ placing: { valid: false } });
  }

  private stopPlacing(): void {
    this.placing = null;
    this.ghost?.destroy();
    this.ghost = null;
    useGameStore.getState().setPersonalDeskUi({ placing: null });
  }

  private updateGhost(playerPos: { x: number; y: number }): void {
    const placing = this.placing!;
    const area = this.areas.find((a) => a.id === placing.areaId);
    if (!area || !this.ghost) return;
    const size = deskSizeOf(area.properties);
    const t = this.deps.tile;

    // Souris : le bureau suit le pointeur. Tactile : centré sur l'avatar.
    let anchor = playerPos;
    if (!this.touch) {
      const p = this.scene.input.activePointer;
      const world = this.scene.cameras.main.getWorldPoint(p.x, p.y);
      anchor = { x: world.x, y: world.y };
    }
    const tileX = Math.floor(anchor.x / t - size.width / 2 + 0.5);
    const tileY = Math.floor(anchor.y / t - size.height / 2 + 0.5);
    const rect = { x: tileX - area.x, y: tileY - area.y };

    const valid = this.isValidSpot(area, rect, size, placing.blocked);
    if (!placing.rect || placing.rect.x !== rect.x || placing.rect.y !== rect.y) {
      placing.rect = rect;
      const color = valid ? COLOR_OK : COLOR_KO;
      this.ghost.clear();
      this.ghost.fillStyle(color, 0.25);
      this.ghost.fillRect(tileX * t, tileY * t, size.width * t, size.height * t);
      this.ghost.lineStyle(2, color, 0.9);
      this.ghost.strokeRect(tileX * t, tileY * t, size.width * t, size.height * t);
      if (useGameStore.getState().personalDeskUi.placing?.valid !== valid) {
        useGameStore.getState().setPersonalDeskUi({ placing: { valid } });
      }
    }
  }

  private isValidSpot(area: DeskArea, rect: { x: number; y: number }, size: { width: number; height: number }, blocked: (cx: number, cy: number) => boolean): boolean {
    const r = { ...rect, ...size };
    if (!deskFitsArea(r, area)) return false;
    if (this.desks.some((d) => d.areaZoneId === area.id && desksOverlap(r, { x: d.x, y: d.y, ...size }))) return false;
    // Ni sur un emplacement, même libre (réservés aux membres).
    if (this.slots.some((sl) => sl.areaZoneId === area.id && desksOverlap(r, { x: sl.x, y: sl.y, ...size }))) return false;
    for (let y = 0; y < size.height; y++) {
      for (let x = 0; x < size.width; x++) {
        if (blocked(area.x + rect.x + x, area.y + rect.y + y)) return false;
      }
    }
    return true;
  }

  private async confirmPlacing(): Promise<void> {
    const placing = this.placing;
    if (!placing?.rect || !useGameStore.getState().personalDeskUi.placing?.valid) return;
    const res = await this.api('POST', '/world/desks/personal', { areaZoneId: placing.areaId, ...placing.rect }).catch(() => null);
    if (this.placing !== placing) return; // annulé entre-temps
    if (!res) return toast('Erreur réseau : ton bureau n’a pas été posé.', 'danger');
    if (!res.ok) {
      const { error } = await res.json().catch(() => ({ error: '' })) as { error?: string };
      return toast(PLACE_ERRORS[error ?? ''] ?? `Ton bureau n’a pas pu être posé (HTTP ${res.status}).`, 'warning');
    }
    this.stopPlacing();
    toast('Ton bureau est installé.', 'success');
    await this.load();
  }

  /** Prendre l'emplacement libre où se tient l'avatar. */
  private async claim(): Promise<void> {
    const slotId = this.currentSlotId;
    if (!slotId) return;
    const res = await this.api('POST', '/world/desks/personal', { slotId }).catch(() => null);
    if (!res) return toast('Erreur réseau : ce bureau n\u2019a pas pu être pris.', 'danger');
    if (!res.ok) {
      const { error } = await res.json().catch(() => ({ error: '' })) as { error?: string };
      toast(PLACE_ERRORS[error ?? ''] ?? `Ce bureau n\u2019a pas pu être pris (HTTP ${res.status}).`, 'warning');
      if (error === 'SLOT_TAKEN' || error === 'SLOT_NOT_FOUND') await this.load();
      return;
    }
    toast('Ce bureau est à toi. Tu peux le personnaliser.', 'success');
    await this.load();
  }

  private async release(): Promise<void> {
    const res = await this.api('DELETE', '/world/desks/personal').catch(() => null);
    if (!res?.ok) return toast('Ton bureau n’a pas pu être libéré.', 'danger');
    toast('Ton bureau est libéré.', 'success');
    await this.load();
  }

  // ── Personnaliser son bureau ───────────────────────────────

  private myDesk(): PersonalDeskDTO | undefined {
    return this.desks.find((d) => d.userId === this.deps.localUserId && !d.outOfArea);
  }

  private async startCustomizing(): Promise<void> {
    const desk = this.myDesk();
    if (!desk || this.customizing || this.placing) return;
    const res = await this.api('GET', '/world/desks/catalog').catch(() => null);
    if (!res?.ok) return toast('Le catalogue des objets n\u2019a pas pu être chargé.', 'danger');
    const { furnitureIds } = await res.json() as { furnitureIds: string[] };
    if (this.customizing || !this.myDesk()) return;

    const t = this.deps.tile;
    const grid = this.scene.add.graphics().setDepth(DEPTH_GHOST - 1);
    grid.lineStyle(1, COLOR_OK, 0.5);
    for (let x = 0; x <= desk.width; x++) grid.lineBetween((desk.tileX + x) * t, desk.tileY * t, (desk.tileX + x) * t, (desk.tileY + desk.height) * t);
    for (let y = 0; y <= desk.height; y++) grid.lineBetween(desk.tileX * t, (desk.tileY + y) * t, (desk.tileX + desk.width) * t, (desk.tileY + y) * t);

    this.customizing = {
      deskId: desk.id,
      draft: desk.items.map((i) => ({ furnitureId: i.furnitureId, dx: i.dx, dy: i.dy })),
      selectedId: null,
      carried: null,
      allowed: new Set(furnitureIds),
      objects: [],
      ghost: null,
      ghostKey: null,
      grid,
      saving: false,
    };
    this.deps.focusCamera((desk.tileX + desk.width / 2) * t, (desk.tileY + desk.height / 2) * t);
    await this.render(); // retire les objets du bureau (et leurs collisions)
    await this.renderDraft();
    this.publishCustomizing(furnitureIds, desk);
  }

  /** `rerender` : réafficher les objets enregistrés ; `releaseCamera` : faux quand la scène se ferme. */
  private stopCustomizing(rerender: boolean, releaseCamera = true): void {
    const c = this.customizing;
    if (!c) return;
    this.customizing = null;
    for (const o of c.objects) o.destroy();
    c.ghost?.destroy();
    c.grid.destroy();
    useGameStore.getState().setPersonalDeskUi({ customizing: null });
    if (releaseCamera) this.deps.releaseCamera();
    if (rerender) void this.render();
  }

  private async saveCustomizing(): Promise<void> {
    const c = this.customizing;
    if (!c || c.saving) return;
    if (c.carried) this.dropCarried(c.carried.from); // objet en main : remis à sa place
    c.saving = true;
    this.publishCustomizing();
    // Calque et collision : décidés par l'API selon l'objet.
    const items = c.draft.map((d) => ({ furnitureId: d.furnitureId, dx: d.dx, dy: d.dy }));
    const res = await this.api('PUT', '/world/desks/personal/items', { items }).catch(() => null);
    if (this.customizing !== c) return;
    c.saving = false;
    if (!res?.ok) {
      const { error } = res ? await res.json().catch(() => ({ error: '' })) as { error?: string } : { error: '' };
      this.publishCustomizing();
      return toast(SAVE_ERRORS[error ?? ''] ?? 'Ton bureau n\u2019a pas pu être enregistré.', 'danger');
    }
    this.stopCustomizing(false);
    toast('Ton bureau est enregistré.', 'success');
    await this.load();
  }

  private selectFurniture(furnitureId: string | null): void {
    const c = this.customizing;
    if (!c) return;
    if (c.carried) this.dropCarried(c.carried.from);
    c.selectedId = furnitureId && c.allowed.has(furnitureId) ? furnitureId : null;
    this.publishCustomizing();
  }

  /** Case visée (bas-gauche de l'objet), relative au bureau. */
  private pointerCell(desk: PersonalDeskDTO): { dx: number; dy: number } {
    const p = this.scene.input.activePointer;
    const world = this.scene.cameras.main.getWorldPoint(p.x, p.y);
    const t = this.deps.tile;
    return { dx: Math.floor(world.x / t) - desk.tileX, dy: Math.floor(world.y / t) - desk.tileY };
  }

  private canPlace(furnitureId: string, cell: { dx: number; dy: number }, desk: PersonalDeskDTO, adding: boolean): boolean {
    const c = this.customizing!;
    const entry = findFurniture(furnitureId);
    if (!entry || !c.allowed.has(furnitureId)) return false;
    if (adding && c.draft.length >= MAX_DESK_ITEMS) return false;
    return itemFitsDesk(cell, furnitureFootprint(entry), desk);
  }

  private clickCustomizing(): void {
    const c = this.customizing;
    const desk = this.desks.find((d) => d.id === c?.deskId);
    if (!c || !desk || c.saving) return;
    const cell = this.pointerCell(desk);

    if (c.carried) {
      if (this.canPlace(c.carried.furnitureId, cell, desk, false)) this.dropCarried({ furnitureId: c.carried.furnitureId, ...cell });
      return;
    }
    if (c.selectedId) {
      if (!this.canPlace(c.selectedId, cell, desk, true)) {
        if (c.draft.length >= MAX_DESK_ITEMS) toast(`${MAX_DESK_ITEMS} objets au maximum par bureau.`, 'warning');
        return;
      }
      c.draft.push({ furnitureId: c.selectedId, ...cell });
      void this.renderDraft();
      this.publishCustomizing();
      return;
    }
    // Rien en main : ramasser l'objet sous le pointeur (le dernier posé d'abord).
    for (let i = c.draft.length - 1; i >= 0; i--) {
      const item = c.draft[i]!;
      const entry = findFurniture(item.furnitureId);
      if (!entry) continue;
      const f = furnitureFootprint(entry);
      if (cell.dx >= item.dx && cell.dx < item.dx + f.width && cell.dy <= item.dy && cell.dy > item.dy - f.height) {
        c.draft.splice(i, 1);
        c.carried = { ...item, from: { ...item } };
        void this.renderDraft();
        this.publishCustomizing();
        return;
      }
    }
  }

  private dropCarried(at: DraftItem): void {
    const c = this.customizing;
    if (!c?.carried) return;
    c.draft.push(at);
    c.carried = null;
    void this.renderDraft();
    this.publishCustomizing();
  }

  private removeCarried(): void {
    const c = this.customizing;
    if (!c?.carried) return;
    c.carried = null;
    this.publishCustomizing();
  }

  private keyCustomizing(e: KeyboardEvent): void {
    const c = this.customizing!;
    if (e.key === 'Escape') {
      if (c.carried) this.dropCarried(c.carried.from);
      else if (c.selectedId) this.selectFurniture(null);
    } else if ((e.key === 'Delete' || e.key === 'Backspace') && c.carried) {
      e.preventDefault();
      this.removeCarried();
    }
  }

  private async renderDraft(): Promise<void> {
    const c = this.customizing;
    const desk = this.desks.find((d) => d.id === c?.deskId);
    if (!c || !desk) return;
    const entries = c.draft.map((d) => findFurniture(d.furnitureId)).filter((e) => e !== undefined);
    await ensureFurnitureTextures(this.scene, entries);
    if (this.customizing !== c) return;
    for (const o of c.objects) o.destroy();
    c.objects = [];
    const t = this.deps.tile;
    const offsets = deskItemDepthOffsets(c.draft);
    for (const [index, item] of c.draft.entries()) {
      const entry = findFurniture(item.furnitureId);
      if (!entry) continue;
      const bottomY = (desk.tileY + item.dy + 1) * t;
      const sprite = spawnFurnitureImage(this.scene, entry, (desk.tileX + item.dx) * t, bottomY);
      sprite.setDepth(isDeskRug(entry) ? FLOOR_DEPTHS.floorObjects : 100 + bottomY / 10 + (offsets[index] ?? 0));
      if (!c.allowed.has(item.furnitureId)) sprite.setTint(COLOR_KO); // retiré du catalogue depuis
      c.objects.push(sprite);
    }
  }

  private updateCustomizeGhost(): void {
    const c = this.customizing!;
    const desk = this.desks.find((d) => d.id === c.deskId);
    const id = c.carried?.furnitureId ?? c.selectedId;
    const entry = id ? findFurniture(id) : undefined;
    if (!desk || !entry) {
      c.ghost?.setVisible(false);
      return;
    }
    if (c.ghostKey !== entry.id) {
      c.ghost?.destroy();
      c.ghost = null;
      c.ghostKey = entry.id;
      void ensureFurnitureTextures(this.scene, [entry]).then(() => {
        if (this.customizing !== c || c.ghostKey !== entry.id) return;
        c.ghost = spawnFurnitureImage(this.scene, entry, 0, 0).setAlpha(0.7).setDepth(DEPTH_GHOST);
      });
    }
    if (!c.ghost) return;
    const cell = this.pointerCell(desk);
    const t = this.deps.tile;
    c.ghost.setVisible(true);
    c.ghost.setPosition((desk.tileX + cell.dx) * t, (desk.tileY + cell.dy + 1) * t);
    if (this.canPlace(entry.id, cell, desk, !c.carried)) c.ghost.clearTint();
    else c.ghost.setTint(COLOR_KO);
  }

  /** État du panneau (store) : objets autorisés, compteur, objet en main. */
  private publishCustomizing(furnitureIds?: string[], desk?: PersonalDeskDTO): void {
    const c = this.customizing;
    if (!c) return;
    const prev = useGameStore.getState().personalDeskUi.customizing;
    const d = desk ?? this.desks.find((x) => x.id === c.deskId);
    useGameStore.getState().setPersonalDeskUi({
      customizing: {
        furnitureIds: furnitureIds ?? prev?.furnitureIds ?? [...c.allowed],
        deskWidth: d?.width ?? prev?.deskWidth ?? 3,
        deskHeight: d?.height ?? prev?.deskHeight ?? 2,
        selectedId: c.selectedId,
        carrying: !!c.carried,
        count: c.draft.length + (c.carried ? 1 : 0),
        notAllowed: c.draft.filter((i) => !c.allowed.has(i.furnitureId)).length,
        saving: c.saving,
      },
    });
  }

  // ── Visites ────────────────────────────────────────────────

  private checkVisitors(): void {
    const mine = this.desks.find((d) => d.userId === this.deps.localUserId && !d.outOfArea);
    if (!mine) { this.visitorsAtMyDesk.clear(); return; }
    const t = this.deps.tile;
    const here = new Set<string>();
    for (const p of this.deps.remotePlayers()) {
      const cx = Math.floor(p.x / t);
      const cy = Math.floor(p.y / t);
      if (cx < mine.tileX || cx >= mine.tileX + mine.width || cy < mine.tileY || cy >= mine.tileY + mine.height) continue;
      here.add(p.userId);
      if (!this.visitorsAtMyDesk.has(p.userId)) {
        EventBus.emit('ui:toast', { message: `${p.name} passe à ton bureau`, duration: 4000 });
      }
    }
    this.visitorsAtMyDesk = here;
  }

  // ── Divers ─────────────────────────────────────────────────

  private api(method: string, path: string, body?: unknown): Promise<Response> {
    const jwt = useGameStore.getState().jwt;
    return fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        Authorization: `Bearer ${jwt ?? ''}`,
        'X-Tenant-ID': getTenantSlug(),
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  }

  destroy(): void {
    this.generation++;
    this.stopPlacing();
    this.stopCustomizing(false, false);
    for (const obj of this.rendered) obj.destroy();
    this.rendered = [];
    for (const fn of this.cleanups) fn();
    this.cleanups = [];
    useGameStore.getState().setPersonalDeskUi({ area: null, desk: null, slot: null, areaHasFreeSlot: false, placing: null });
  }
}

const SAVE_ERRORS: Record<string, string> = {
  FURNITURE_NOT_ALLOWED: 'Un objet n\u2019est plus autorisé (en rouge) : retire-le pour enregistrer.',
  ITEM_OUT_OF_DESK: 'Un objet dépasse de ton bureau.',
  TOO_MANY_ITEMS: `${MAX_DESK_ITEMS} objets au maximum par bureau.`,
  NO_DESK: 'Ton bureau n\u2019existe plus.',
};

const PLACE_ERRORS: Record<string, string> = {
  DESK_OVERLAP: 'Cet emplacement empiète sur un autre bureau.',
  DESK_ON_OBSTACLE: 'Il y a un mur, de l\u2019eau, un objet ou une porte à cet emplacement.',
  OUT_OF_AREA: 'Ton bureau doit tenir entièrement dans l’espace bureaux.',
  ALREADY_HAS_DESK: 'Tu as déjà un bureau. Libère-le d’abord pour en poser un autre.',
  AREA_NOT_FOUND: 'Cet espace bureaux n’existe plus.',
  SLOT_OVERLAP: 'Cet endroit est un emplacement réservé : prends-le, ou pose ton bureau ailleurs.',
  SLOT_TAKEN: 'Quelqu\u2019un vient de prendre ce bureau.',
  SLOT_NOT_FOUND: 'Cet emplacement n\u2019existe plus.',
  FREE_PLACEMENT_FORBIDDEN: 'Choisis un emplacement libre pour ton bureau.',
};

/** Rectangle en pointillés (Phaser n'a pas de trait pointillé). */
function dashedRect(g: Phaser.GameObjects.Graphics, x: number, y: number, w: number, h: number, color: number, alpha: number): void {
  const dash = 4, gap = 4;
  g.lineStyle(1, color, alpha);
  const line = (x1: number, y1: number, x2: number, y2: number) => {
    const len = Math.hypot(x2 - x1, y2 - y1);
    for (let d = 0; d < len; d += dash + gap) {
      const a = d / len, b = Math.min(d + dash, len) / len;
      g.lineBetween(x1 + (x2 - x1) * a, y1 + (y2 - y1) * a, x1 + (x2 - x1) * b, y1 + (y2 - y1) * b);
    }
  };
  line(x, y, x + w, y);
  line(x + w, y, x + w, y + h);
  line(x + w, y + h, x, y + h);
  line(x, y + h, x, y);
}

/** Prénom (premier mot) d'un nom affiché. */
function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] || name;
}

/** Raccourcit le texte (avec « … ») jusqu'à ce que l'étiquette tienne dans `maxWidth` px. */
function fitLabel(label: Phaser.GameObjects.Text, maxWidth: number): void {
  let text = label.text;
  while (label.width > maxWidth && text.length > 1) {
    text = text.slice(0, -1);
    label.setText(`${text.trimEnd()}…`);
  }
}

function toast(message: string, tone: 'info' | 'success' | 'warning' | 'danger'): void {
  EventBus.emit('ui:toast', { message, tone });
}
